import type { Topic } from '@/@types/topic'
import { AttachmentType, FileAttachment, type Attachment, type LinkInfo } from '@/states/attachment'
import { getVideoMetadata } from '@/utils/media'
import type { UseFormRegister } from 'react-hook-form'
import { z } from 'zod'

export interface PostCreatePayload {
	content: string
	coord?: {
		coordType: string
		lat: string
		lng: string
	}
	pictureKeys?: string[]
	videoKey?: string
	linkInfo?: LinkInfo & {
		supplementary: boolean
		urlsInText: string[]
		linkType: 'default'
	}
	submitToTopic?: string
	syncToPersonalUpdate: boolean
}

export interface PostUpdatePayload {
	id: string
	content: string
	editType: 'IMAGE' | 'VIDEO' | 'LINK' | 'TEXT'
	pictureKeys?: string[]
	videoKey?: string
	submitToTopic?: string
	linkInfo?: LinkInfo & {
		supplementary: boolean
		urlsInText: string[]
		linkType: 'default'
	}
}

// 视频校验函数
async function validateVideo(attachment: FileAttachment, ctx: z.RefinementCtx) {
	// 检查视频格式
	if (attachment.file && !attachment.file.name.toLowerCase().endsWith('.mp4')) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '仅支持 MP4 格式的视频',
		})
	}

	// 检查视频大小
	if (attachment.file && attachment.file.size > 100 * 1024 * 1024) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '视频大小不能超过 100MB',
		})
	}

	const metadata = await getVideoMetadata(attachment.file)
	// 检查视频分辨率
	if (metadata.width && metadata.height) {
		const minSide = Math.min(metadata.width, metadata.height)
		const maxSide = Math.max(metadata.width, metadata.height)

		if (minSide > 1080 || maxSide > 1920) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频分辨率不能超过 1920x1080',
			})
		}
	}

	// 检查视频时长
	if (metadata.duration) {
		if (metadata.duration < 3) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '视频时长不能少于 3 秒',
			})
		}
	}
}

// 定义表单验证 schema
export const postFormSchema = z.object({
	// 帖子内容验证
	content: z.string(),

	// 附件验证
	attachments: z.array(z.custom<Attachment>()).superRefine(async (attachments, ctx) => {
		const hasPictures = attachments.some((item) => item.type === AttachmentType.PICTURE)
		const hasVideo = attachments.some((item) => item.type === AttachmentType.VIDEO)
		const hasLink = attachments.some((item) => item.type === AttachmentType.LINK)

		if (hasPictures && hasVideo) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '不能同时添加图片和视频',
			})
		}

		if (hasPictures && attachments.length > 9) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '最多添加9张图片',
			})
		}

		if (hasVideo) {
			if (attachments.length > 1) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: '最多添加1个视频',
				})
			}

			// 对所有视频附件进行校验
			for (const attachment of attachments.filter((item) => item.type === AttachmentType.VIDEO)) {
				await validateVideo(attachment as FileAttachment, ctx)
			}
		}

		if (hasLink && attachments.length > 1) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: '最多添加1个链接',
			})
		}
	}),

	// 话题验证
	topic: z.custom<Topic>().optional(),

	// ID 用于区分创建/编辑模式
	id: z.string().optional(),
})

// 导出类型
export type PostFormValue = z.infer<typeof postFormSchema>

export type FieldBasicProps = ReturnType<UseFormRegister<PostFormValue>>
